import { Component, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';

import { type Observable } from 'rxjs';

import { ConfigService } from '@fcom/core';
import { EMAIL_REGEX, NAME_PATTERN } from '@fcom/core/utils';
import { finnairPlusNumberValidator, PrefillProfileData } from '@fcom/service-forms';
import {
  ATTACHMENT_ALLOWED_FILE_EXTENSIONS,
  ATTACHMENT_MAX_SIZE_IN_BYTES,
  TEXTAREA_LIMIT_3000,
} from '@fcom/ui-components';

import {
  ClaimMissingPartnerPointsFormData,
  ClaimMissingPartnerPointsFormField,
  ClaimMissingPartnerPointsResponseData,
  FormBuilderGroup,
  FormBuilderInput,
} from '../interfaces';
import { MissingPointsService } from '../services/missing-points.service';
import { SubmitService } from '../services/submit.service';
import { type FormBuilderComponent } from './form-builder.component';

@Component({
  selector: 'fin-claim-missing-partner-points-form',
  template: `
    <fin-form-builder
      [controlConfig]="controlConfig"
      [submitHandler]="submitHandler"
      [hasSuccessMessage]="false"
      formName="claim-missing-partner-points"
      translationPrefix="SERVICE_FORMS.serviceForms"
      #formBuilder
    >
    </fin-form-builder>
  `,
})
export class ClaimMissingPartnerPointsFormComponent {
  readonly allowedFileTypes: string[] = ATTACHMENT_ALLOWED_FILE_EXTENSIONS;
  readonly maxSizeInBytes: number = ATTACHMENT_MAX_SIZE_IN_BYTES;
  readonly textareaLimit3000: number = TEXTAREA_LIMIT_3000;

  controlConfig: FormBuilderGroup[] = [
    {
      controlName: 'claimMissingPartnerPoints',
      fields: [
        {
          type: FormBuilderInput.TEXT,
          controlName: ClaimMissingPartnerPointsFormField.MEMBER_NAME,
          translationPrefix: 'SERVICE_FORMS.serviceForms.fields.name',
          controlConfig: ['', [Validators.required, Validators.pattern(NAME_PATTERN)]],
          prefillFromProfile: PrefillProfileData.FIRST_LAST_NAME,
        },
        {
          type: FormBuilderInput.TEXT,
          controlName: ClaimMissingPartnerPointsFormField.MEMBER_NUMBER,
          translationPrefix: 'SERVICE_FORMS.serviceForms.fields.memberNumber',
          placeHolder: 'SERVICE_FORMS.serviceForms.fields.memberNumber.placeholder',
          controlConfig: ['', [Validators.required, finnairPlusNumberValidator()]],
          prefillFromProfile: PrefillProfileData.MEMBER_NUMBER,
        },
        {
          type: FormBuilderInput.TEXT,
          controlName: ClaimMissingPartnerPointsFormField.EMAIL,
          translationPrefix: 'SERVICE_FORMS.serviceForms.fields.email',
          controlConfig: ['', [Validators.required, Validators.pattern(EMAIL_REGEX)]],
          prefillFromProfile: PrefillProfileData.EMAIL,
        },
        {
          type: FormBuilderInput.FILE_UPLOADER,
          allowedFileTypes: this.allowedFileTypes,
          controlConfig: [[], Validators.required],
          controlName: ClaimMissingPartnerPointsFormField.ATTACHMENT,
          maxAttachments: 1,
          maxSizeInBytes: this.maxSizeInBytes,
        },
        {
          type: FormBuilderInput.TEXTAREA,
          controlName: ClaimMissingPartnerPointsFormField.ADDITIONAL_INFORMATION,
          translationPrefix: 'SERVICE_FORMS.serviceForms.fields.additionalInformation',
          controlConfig: ['', [Validators.maxLength(this.textareaLimit3000)]],
          wordCount: this.textareaLimit3000,
        },
      ],
    },
  ];

  @ViewChild('formBuilder', { static: false }) formBuilder: FormBuilderComponent;

  constructor(
    private submitService: SubmitService,
    private configService: ConfigService,
    private missingPointsService: MissingPointsService
  ) {
    this.submitHandler = this.submitHandler.bind(this);
  }

  submitHandler(values: ClaimMissingPartnerPointsFormData): Observable<ClaimMissingPartnerPointsResponseData> {
    if (this.configService.cfg.enableNewClaimMissingPointFlow) {
      return this.missingPointsService.claimPartnerMissingAvios(values);
    } else {
      return this.submitService.claimMissingPartnerPoints(values);
    }
  }
}
